import { useState, Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { getCitiesList } from '../services/api_resources';
import { getLocationsCompanyPaginated } from '../services/api_anagrafica_employee';
import ComboboxAutocompleteAPI from './combobox_autocomplete_api';
import Swal from 'sweetalert2';
import Empty from '../components/empty';
import Fetching from './fetching';
import { deleteLocationCompany, updateLocationCompany, createLocationCompany } from '../services/api_company_locations';
import { hasPermission } from '../utils';


const LocationsCompanyForm = ({ token, profile }) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalLocations, setTotalLocations] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedLocation, setEditedLocation] = useState(selectedLocation);
    const [errors, setErrors] = useState({});
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [newLocation, setNewLocation] = useState({
        name: '',
        address: '',
        city: '',
        cap: '',
        phone: '',
        external_reference: '',
        email: ''
    });

    // se nel piano è presentee attendance mostra l'elenco delle sedi
    const hasAttendanceLevelPermission = hasPermission('attendance', profile);

    useEffect(() => {
        const fetchLocations = async () => {
            setLoading(true);
            try {
                const response = await getLocationsCompanyPaginated(token, page, pageSize, 'attendance');
                setLocations((response.results || []).sort((a, b) => b.id - a.id));
                setTotalLocations(response.count || 0);
            } catch (error) {
                console.error('Errore nel recupero delle sedi:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLocations();
    }, [token, page, pageSize]);

    const handleDeleteLocation = async (id) => {
        Swal.fire({
            icon: "warning",
            title: 'Conferma eliminazione',
            text: `Stai eliminando la sede lavorativa. Sei sicuro di voler procedere?`,
            showCancelButton: true,
            confirmButtonText: "Sì",
            cancelButtonText: "Annulla",
            cancelButtonColor: "#d1d5db",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteLocationCompany(token, id);
                    
                    const newTotalLocations = totalLocations - 1;
                    const updatedLocations = locations.filter((location) => location.id !== id);
    
                    const maxPage = Math.ceil(newTotalLocations / pageSize);
                    const newPage = page > maxPage ? maxPage : page;
    
                    setLocations(updatedLocations);
                    setTotalLocations(newTotalLocations);
                    setPage(newPage);
    
                    Swal.fire({
                        position: "center",
                        icon: "success",
                        title: "Sede eliminata con successo!",
                        showConfirmButton: false,
                        timer: 1500
                    });
                } catch (error) {
                    console.error('Errore durante l\'eliminazione della sede', error);
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        text: "Errore durante l'eliminazione della sede",
                    });
                }
            }
        });
    };
    
    
    const handleUpdateLocation = async (updatedLocation, closeModalCallback) => {
        setLoading(true);
        setErrors({});
            
        const loadingSwal = Swal.fire({
          title: 'Aggiornamento in corso...',
          text: 'Attendere prego...',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });
        
        try {
          const dataToSend = {
            ...updatedLocation,
            city: updatedLocation.city ? updatedLocation.city?.id : null,
          };
          delete dataToSend.city_data;
          
          const updatedData = await updateLocationCompany(token, updatedLocation.id, dataToSend);
          
          setLocations((prevLocations) =>
            prevLocations.map((location) =>
              location.id === updatedLocation.id ? updatedData : location
            )
          );
      
          closeModalCallback();
          loadingSwal.close();
      
          Swal.fire({
            position: 'center',
            icon: 'success',
            title: 'Sede aggiornata con successo!',
            showConfirmButton: false,
            timer: 1500,
          });
        } catch (error) {
          console.error('Errore durante l\'aggiornamento della sede', error);
      
          let errorDetails = error.response?.data
            ? Object.values(error.response.data).flat()
            : [];
          const errorMessage =
            errorDetails.length > 0
              ? errorDetails.join('\n')
              : 'Errore durante l\'aggiornamento della sede. Riprova più tardi.';
      
          loadingSwal.close();
      
          Swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: errorMessage,
          });
        } finally {
          setLoading(false);
        }
    };  
    
    const handleSaveNewLocation = async (locationData, closeModalCallback) => {
        setLoading(true); 
        setErrors({});
          
        const loadingSwal = Swal.fire({
            title: 'Salvataggio in corso...',
            text: 'Attendere prego...',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
    
        try {
            const locationWithCompanyId = {
                ...locationData,
                company: profile.company.id,
                city: locationData.city ? locationData.city?.id : null,
            };
            delete locationWithCompanyId.city_data;
    
            const newLocation = await createLocationCompany(token, locationWithCompanyId, 'attendance');
    
            setLocations((prevLocations) => 
                [newLocation, ...prevLocations].sort((a, b) => b.id - a.id)
            );
    
            setTotalLocations(prev => prev + 1)
            setPage(1);
    
            loadingSwal.close();
    
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: 'Sede creata con successo!',
                showConfirmButton: false,
                timer: 1500,
            });
    
            closeModalCallback();
        } catch (error) {
            console.error('Errore durante il salvataggio della sede', error);
    
            let errorDetails = error.response?.data ? Object.values(error.response.data).flat() : [];
            const errorMessage = errorDetails.length > 0
                ? errorDetails.join('\n')
                : 'Errore durante il salvataggio della sede. Riprova più tardi.';
    
            loadingSwal.close();
    
            Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: errorMessage,
            });
        } finally {
            setLoading(false); 
        }
    };
    
    useEffect(() => {
        setEditedLocation(selectedLocation);
    }, [selectedLocation]);

    const handleChange = (field, value) => {
        setEditedLocation((prev) => ({
          ...prev,
          [field]: value,
        }));
    };

    const handleOpenModal = (location) => {
        setSelectedLocation(location);
        setOpen(true);
    };

    const handleSave = () => {
        const errors = validateForm();
        
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            return;
        }
        handleSaveNewLocation(editedLocation, handleCloseModal);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setErrors({})
        setSelectedLocation(null);
        setIsEditing(false);
    };

    const handleInputChange = (key, value) => {
        setNewLocation((prev) => ({
            ...prev,
            [key]: value,
            ...(key === 'city' && { city_data: value }),
        }));
    };

    const openCreateLocationForm = () => {
        setOpenCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setOpenCreateModal(false);
    };

    const validateForm = () => {
        const errors = {};
    
        const locationToValidate = isEditing ? editedLocation : newLocation;
    
        if (!locationToValidate?.name || locationToValidate?.name.trim() === '') {
            errors.name = 'Il nome è obbligatorio';
        }
    
        if (!locationToValidate?.address || locationToValidate?.address.trim() === '') {
            errors.address = 'L\'indirizzo è obbligatorio';
        }
    
        if (Object.keys(errors).length > 0) {
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Compilare i campi obbligatori.',
            });
        }
    
        return errors;
    };
    
    const handleCreate = () => {
        const errors = validateForm();
        
        if (Object.keys(errors).length > 0) {
          setErrors(errors);
          return;
        }
      
        handleSaveNewLocation(newLocation, resetFormAndCloseModal);
    };
      

    const resetFormAndCloseModal = () => {
        setNewLocation({
            name: '',
            address: '',
            city: '',
            cap: '',
            phone: '',
            external_reference: '',
            email: '',
        });
        setErrors({});
        handleCloseCreateModal();
    };

    return (
        <>
            {!hasAttendanceLevelPermission ? (
                ''
            ) : (
                <div className="flex flex-wrap -mx-4">
                    <div className="w-full md:w-2/3 px-4 mb-5">
                        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
                            <div className="border-t border-gray-100">
                                <dl className="divide-y divide-gray-100">
                                {/* Elenco delle sedi */}
                                    <div className="px-4 py-6 sm:px-6">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-base font-semibold leading-7 text-gray-900 uppercase">Elenco Sedi</h3>
                                            <button className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                onClick={openCreateLocationForm}
                                            >
                                                Aggiungi
                                            </button>
                                        </div>

                                    {loading ? (
                                        <Fetching />
                                    ) : locations.length > 0 ? (
                                        <div className='overflow-x-auto'>
                                            <table className="min-w-full min-h-full overflow-x">
                                                <thead className="bg-white">
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900"
                                                            style={{ width: '25%' }}
                                                        >
                                                            Nome
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900"
                                                            style={{ width: '30%' }}
                                                        >
                                                            Città
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900"
                                                            style={{ width: '30%' }}
                                                        >
                                                            Indirizzo
                                                        </th>
                                                        {/* <th
                                                            scope="col"
                                                            className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900"
                                                            style={{ width: '20%' }}
                                                        >
                                                            ID Gestionale Esterno
                                                        </th> */}
                                                        <th
                                                            scope="col"
                                                            className="px-3 py-1.5 text-left text-sm font-semibold text-gray-900"
                                                            style={{ width: '15%' }}
                                                        >
                                                        
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white overflow-x">
                                                {locations.map((location) => (
                                                    <tr key={location.id} className="border-gray-200 border-t">
                                                        <td className="whitespace-normal px-3 py-4 text-sm text-gray-500" style={{ width: '25%' }}>
                                                            {location?.name}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 truncate" style={{ width: '30%' }}>
                                                            {location?.city_data?.name}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" style={{ width: '30%' }}>
                                                            {location?.address}
                                                        </td>
                                                        {/* <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" style={{ width: '20%' }}>
                                                            {location?.external_reference}
                                                        </td> */}
                                                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3" style={{ width: '25%' }}>
                                                            <button
                                                                onClick={() => handleOpenModal(location)}
                                                                className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 me-2"
                                                            >
                                                            Apri
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteLocation(location.id)}
                                                                className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-red-500 hover:text-white"
                                                            >
                                                            Elimina
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className='p-4'>
                                            <Empty />
                                        </div>
                                    )}
                                    {totalLocations > pageSize && (
                                        <div className="flex justify-between mt-4 mb-0 px-8">
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                onClick={() => setPage(page - 1)}
                                                disabled={page === 1}
                                            >
                                                Precedente
                                            </button>
                                            <span className="text-sm font-medium text-gray-900">
                                                Pagina {page} di {Math.ceil(totalLocations / pageSize)}
                                            </span>
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                onClick={() => setPage(page + 1)}
                                                disabled={page * pageSize >= totalLocations}
                                            >
                                                Successivo
                                            </button>
                                        </div>
                                    )}
                                </div>
                                </dl>
                            </div>
                        </div>
                    </div>

                    {/* MODALE PER VISUALIZZAZIONE E MODIFCA SEDE */}
                    <Transition.Root show={open} as={Fragment}>
                        <Dialog as="div" className="relative z-10" onClose={handleCloseModal}>
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                            </Transition.Child>

                            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                    <Transition.Child
                                        as={Fragment}
                                        enter="ease-out duration-300"
                                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                                        leave="ease-in duration-200"
                                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    >
                                        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                            {selectedLocation && (
                                            <div>
                                                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Dettagli Sede</h3>

                                                <div className="mb-4">
                                                    <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">Nome</label>
                                                    {isEditing ? (
                                                        <input
                                                        type="text"
                                                        value={editedLocation?.name || ''}
                                                        onChange={(e) => handleChange('name', e.target.value)}
                                                        className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.name}</div>
                                                    )}
                                                    {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                                                </div>

                                                <div className="mb-4">
                                                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">Indirizzo</label>
                                                    {isEditing ? (
                                                        <input
                                                        type="text"
                                                        value={editedLocation?.address || ''}
                                                        onChange={(e) => handleChange('address', e.target.value)}
                                                        className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.address}</div>
                                                    )}
                                                    {errors.address && <p className="text-sm text-red-500">{errors.address}</p>}
                                                </div>

                                                <div className="mb-4">
                                                    <label htmlFor="city" className="block text-sm font-medium text-gray-700">Città</label>
                                                    {isEditing ? (
                                                        <div className='border border-gray-500 rounded-md'>
                                                            <ComboboxAutocompleteAPI
                                                                getData={(query) => getCitiesList(token, query)}
                                                                getDisplay={(res) => `${res?.codice_catastale} - ${res?.name}`}
                                                                getId={(res) => res?.id}
                                                                onChange={(id) => handleChange('city', id || null)}
                                                                currentValue={editedLocation?.city === null ? editedLocation?.city : editedLocation?.city_data || ''}
                                                                className="border rounded-md"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">
                                                            {selectedLocation?.city ? `${selectedLocation.city_data.codice_catastale} - ${selectedLocation.city_data.name}` : ''}
                                                        </div>
                                                    )}
                                                    {errors.city && <p className="text-sm text-red-500">{errors.city}</p>}
                                                </div>

                                                <div className="mb-4">
                                                    <label htmlFor="cap" className="block text-sm font-medium text-gray-700">CAP</label>
                                                    {isEditing ? (
                                                        <input
                                                        type="text"
                                                        value={editedLocation?.cap || ''}
                                                        onChange={(e) => {
                                                            const capValue = e.target.value.replace(/\D/g, '');
                                                            handleChange('cap', capValue);
                                                        }}
                                                        className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.cap}</div>
                                                    )}
                                                    {errors.cap && <p className="text-sm text-red-500">{errors.cap}</p>}
                                                </div>

                                                <div className="mb-4">
                                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Numero Telefono</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="tel"
                                                            value={editedLocation?.phone || ''}
                                                            onChange={(e) => {
                                                                const phoneValue = e.target.value.replace(/\D/g, '');
                                                                handleChange('phone', phoneValue);
                                                            }}
                                                            className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.phone}</div>
                                                    )}
                                                    {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
                                                </div>

                                                {/* <div className="mb-4">
                                                    <label htmlFor="external_reference" className="block text-sm font-medium text-gray-700">ID Gestionale Esterno</label>
                                                    {isEditing ? (
                                                        <input
                                                        type="text"
                                                        value={editedLocation?.external_reference || ''}
                                                        onChange={(e) => handleChange('external_reference', e.target.value)}
                                                        className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.external_reference}</div>
                                                    )}
                                                    {errors.external_reference && <p className="text-sm text-red-500">{errors.external_reference}</p>}
                                                </div> */}

                                                <div className="mb-4">
                                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Indirizzo Email</label>
                                                    {isEditing ? (
                                                        <input
                                                        type="text"
                                                        value={editedLocation?.email}
                                                        onChange={(e) => handleChange('email', e.target.value)}
                                                        className="mt-1 p-2 w-full border rounded-md"
                                                        />
                                                    ) : (
                                                        <div className="mt-1 p-2 w-full border rounded-md h-10">{selectedLocation.email}</div>
                                                    )}
                                                    {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                                                </div>

                                            </div>
                                            )}

                                            <div className="mt-5 sm:mt-6">
                                            <span className="isolate inline-flex rounded-md w-full justify-center">
                                                <button
                                                    type="button"
                                                    className="rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 me-3"
                                                    onClick={handleCloseModal}
                                                >
                                                    Chiudi
                                                </button>
                                                {isEditing ? (
                                                    <button
                                                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                                                        onClick={handleSave}
                                                    >
                                                        Salva
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                                                        onClick={handleEdit}
                                                    >
                                                        Modifica
                                                    </button>
                                                )}
                                            </span>
                                            </div>
                                        </Dialog.Panel>
                                    </Transition.Child>
                                </div>
                            </div>
                        </Dialog>
                    </Transition.Root>


                    {/* MODALE CREAZIONE NUOVA SEDE */}
                    <Transition.Root show={openCreateModal} as={Fragment}>
                        <Dialog as="div" className="relative z-10" onClose={handleCloseCreateModal}>
                            <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                            >
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                            </Transition.Child>

                            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Crea Nuova Sede</h3>
                                        <div className="mb-4">
                                            <label htmlFor="business_name" className="block text-sm font-medium text-gray-700">Nome</label>
                                            <input
                                                type="text"
                                                value={newLocation?.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className="mt-1 p-2 w-full border rounded-md"
                                                required
                                            />
                                            {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">Indirizzo</label>
                                            <input
                                                type="text"
                                                value={newLocation?.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                className="mt-1 p-2 w-full border rounded-md"
                                            />
                                            {errors.address && <p className="text-sm text-red-500">{errors.address}</p>}
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="city" className="block text-sm font-medium text-gray-700">Città</label>
                                            <div className='border border-gray-500 rounded-md'>
                                                <ComboboxAutocompleteAPI
                                                    getData={(query) => getCitiesList(token, query)}
                                                    getDisplay={(res) => `${res?.codice_catastale} - ${res?.name}`}
                                                    getId={(res) => res?.id}
                                                    onChange={(id) => handleInputChange('city', id || null)}
                                                    currentValue={newLocation?.city_data || null}
                                                    className="border rounded-md"
                                                />
                                            </div>
                                            {errors.city && <p className="text-sm text-red-500">{errors.city}</p>}
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="cap" className="block text-sm font-medium text-gray-700">CAP</label>
                                    
                                            <input
                                                type="text"
                                                value={newLocation?.cap}
                                                onChange={(e) => {
                                                    const capValue = e.target.value.replace(/\D/g, '');
                                                    handleInputChange('cap', capValue);
                                                }}
                                                className="mt-1 p-2 w-full border rounded-md"
                                                />
                                            {errors.cap && <p className="text-sm text-red-500">{errors.cap}</p>}
                                        </div>

                                        <div className="mb-4">
                                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Numero Telefono</label>
                                            <input
                                                type="tel"
                                                value={newLocation?.phone}
                                                onChange={(e) => {
                                                    const phoneValue = e.target.value.replace(/\D/g, '');
                                                    handleInputChange('phone', phoneValue);
                                                }}
                                                className="mt-1 p-2 w-full border rounded-md"
                                            />
                                            {errors.phone && <p className="text-sm text-red-500">{errors.phone}</p>}
                                        </div>

                                        {/* <div className="mb-4">
                                            <label htmlFor="external_reference" className="block text-sm font-medium text-gray-700">ID Gestionale Esterno</label>
                                            <input
                                                type="text"
                                                value={newLocation?.external_reference}
                                                onChange={(e) => handleInputChange('external_reference', e.target.value)}
                                                className="mt-1 p-2 w-full border rounded-md"
                                            />
                                            {errors.external_reference && <p className="text-sm text-red-500">{errors.external_reference}</p>}
                                        </div> */}

                                        <div className="mb-4">
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Indirizzo Email</label>
                                            <input
                                                type="text"
                                                value={newLocation?.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className="mt-1 p-2 w-full border rounded-md"
                                            />
                                            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
                                        </div>
                                        <div className="mt-5 sm:mt-6">
                                            <span className="isolate inline-flex rounded-md w-full justify-center">
                                                <button
                                                type="button"
                                                className="rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 me-3"
                                                onClick={handleCloseCreateModal}
                                                >
                                                Annulla
                                                </button>
                                                <button
                                                    className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
                                                    onClick={handleCreate}
                                                >
                                                    Salva
                                                </button>
                                            </span>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                                </Transition.Child>
                            </div>
                            </div>
                        </Dialog>
                    </Transition.Root>
                </div>
            )}
        </>
    );
};

export default LocationsCompanyForm;
